/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import { AppAlert, BlockUI } from "@advapacs/common";
import { Buffer } from "buffer";
import OtpInput from "react18-input-otp";
import axios from "axios";
import { useSelector } from "react-redux";
import { IRootState } from "../config/reducers";
import {
  getTwoFaStatus,
  registerTwoFa,
  unRegisterTwoFa,
} from "../pages/login/ducks/operations";
import { useAppDispatch } from "../config/store";

const RegisterTwoFa: FunctionComponent = () => {
  const { t } = useTranslation(["2fa", "general"]);
  const dispatch = useAppDispatch();
  const [qrImage, setQRImage] = useState<string>();
  const [otpSecret, setOtpSecret] = useState();
  const [pinCode, setPinCode] = useState("");

  const {
    loading,
    account,
    twoFaStatus: registered,
    twoFaErrorMessage: otpError,
  } = useSelector((state: IRootState) => state.loginReducer);
  const { isMobileOnly } = useSelector(
    (state: IRootState) => state.layoutSlice
  );
  const { isMobile } = useSelector(
    (state: IRootState) => state.responsiveReducer
  );
  const getStatus = useCallback(() => {
    dispatch(getTwoFaStatus());
  }, [dispatch]);

  const getSecret = useCallback(async () => {
    try {
      const result = await axios.get(`/security/twoFactor/totp/secret`);
      setOtpSecret(result?.data?.secret);
    } catch (err) {
      // eslint-disable-next-line
      console.log(err);
    }
  }, []);

  const getImage = useCallback(async () => {
    try {
      const response = await axios.get(
        `/security/twoFactor/totp/qr?secret=${otpSecret}`,
        {
          responseType: "arraybuffer",
        }
      );
      const b64 = Buffer.from(response.data, "binary").toString("base64");
      setQRImage(b64);
    } catch (err) {
      // eslint-disable-next-line
      console.log(err);
    }
  }, [otpSecret]);

  useEffect(() => {
    if (!registered) {
      getSecret();
    }
  }, [registered, getSecret]);

  useEffect(() => {
    if (otpSecret) {
      getImage();
    }
  }, [otpSecret, getImage]);

  useEffect(() => {
    getStatus();
  }, [getStatus]);

  const verifyPinCode = async () => {
    dispatch(registerTwoFa(parseInt(pinCode, 10), otpSecret));
  };

  const unregister = async () => {
    setPinCode("");
    dispatch(unRegisterTwoFa());
  };

  return (
    <BlockUI isBlocked={loading}>
      <div className="row">
        {account?.enforceTwoFactor && (
          <div className="col-12">
            <div className=" pl-1 pr-1">
              <AppAlert.SimpleAlert
                message={t("two_fa_required")}
                dismissible={false}
                variant="warning"
              />
            </div>
          </div>
        )}
        <div className="col">
          <div className={`${isMobileOnly ? "" : "m-2"} 2fa-container`}>
            <form className="row">
              <div className="col-lg-4 col-md-12 col-xs-12">
                <h4 className="">{t("register_mfa")}</h4>
                <div>{t("registration_description")}</div>
              </div>

              <div className="col-lg-8 col-md-12 col-xs-12">
                {registered && (
                  <button
                    type="button"
                    className={`${isMobile ? "m-t-xs" : ""} btn btn-danger`}
                    onClick={unregister}
                  >
                    {t("unregister")}
                  </button>
                )}
                {!registered && qrImage && (
                  <div className="registration-two-factor">
                    <p>
                      <Trans t={t} i18nKey="2fa:installation_guide">
                        <a href="https://freeotp.github.io/">FreeOTP</a>
                      </Trans>
                    </p>
                    <div className="flex-center-start direction-cl-mobile two-fa-content">
                      <div
                        className="flex-center p-r-sm no-padding-mobile"
                        style={{ minWidth: 200 }}
                      >
                        {otpSecret && (
                          <img
                            src={`data:image/png;base64, ${qrImage}`}
                            alt=""
                          />
                        )}
                      </div>
                      <div className="flex-auto p-lr-sm no-padding-mobile">
                        <div className="alert m-b-none alert-info ">
                          <p>{t("cannot_scan_code")}</p>
                          <p>{t("add_entry_manually")}</p>
                          <p>
                            {t("account")}: Clinician: {account?.username}
                          </p>
                          <p>
                            {t("key")}: {otpSecret}
                          </p>
                          <p>
                            {t("time_base")}: {t("yes")}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6 offset-md-3 mt-2 ml-0">
                        <div className="form-group mb-0">
                          <div
                            tabIndex={0}
                            role="textbox"
                            className="form-group"
                            onKeyPress={(e) =>
                              e.key === "Enter" && verifyPinCode()
                            }
                          >
                            <OtpInput
                              shouldAutoFocus
                              isInputNum
                              className="form-control verify-code"
                              value={pinCode}
                              onChange={(value: string) => {
                                setPinCode(value);
                              }}
                              numInputs={6}
                            />
                          </div>
                          {otpError && (
                            <div className="form-group">
                              <div className="mt-1 mb-1 alert bg-warning text-white alert-styled-left error-container animated fadeInDown mb-0">
                                {otpError?.message}
                              </div>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          className="btn btn-success mt-1"
                          onClick={verifyPinCode}
                          disabled={!pinCode}
                        >
                          {t("register")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </BlockUI>
  );
};

export default RegisterTwoFa;
